import React, { useMemo } from 'react';
import { measuresOf, paginate, reviewElements, reviewText, wrapLines, type DiffRow, type Element, type Paper, type TitlePage } from '../../utils/screenplay';
import { Sheet, TitlePageView, pageMetrics } from './ScreenplayPages';

interface ReviewPagesProps {
  /** The script as the model made it, set against the script as it is */
  rows: DiffRow[];
  titlePage: TitlePage;
  paper: Paper;
  scale: number;
  pageGap: number;
  padTop: number;
}

/** A line added by the model, or words it added */
const ADDED: React.CSSProperties = { background: 'var(--lavender-soft)', color: 'var(--lavender-deep)' };
/** A line the model took out, or words it took out */
const REMOVED: React.CSSProperties = { background: 'rgb(var(--c-blush) / 0.45)', textDecoration: 'line-through', textDecorationThickness: '1px' };

/**
 * The pages as they would be with the model's change, every line it added in lavender, every
 * line it took out still in its place in blush and struck through, and a changed line with its
 * words in reading order: what went struck through where it was, what came in after it, so the
 * line reads as track changes do. Read only: the change is kept or undone whole.
 */
export const ReviewPages: React.FC<ReviewPagesProps> = ({ rows, titlePage, paper, scale, pageGap, padTop }) => {
  const m = pageMetrics(paper, scale);
  const { pages, elements } = useMemo(() => {
    const elements = reviewElements(rows);
    return { elements, pages: paginate({ titlePage, elements, bodyStart: 0 }, paper) };
  }, [rows, titlePage, paper]);

  /** A changed line's runs on one of its wrapped lines: the slice of each run that lies on it */
  const runsOn = (row: DiffRow, element: Element, wrappedRow: number): React.ReactNode => {
    const lines = wrapLines(element.text, measuresOf(element.kind, paper).width);
    const line = lines[wrappedRow];
    if (!line) return '';
    const { spans } = reviewText(row);
    if (!spans.length) return line.text;
    // The runs, and the plain text between them (the breath after a struck run)
    const parts: { from: number; to: number; op: 'same' | 'add' | 'del' | 'gap' }[] = [];
    let at = 0;
    for (const span of spans) {
      if (span.from > at) parts.push({ from: at, to: span.from, op: 'gap' });
      parts.push(span);
      at = span.to;
    }
    if (at < element.text.length) parts.push({ from: at, to: element.text.length, op: 'gap' });
    const out: React.ReactNode[] = [];
    parts.forEach((part, i) => {
      const a = Math.max(part.from, line.start);
      const b = Math.min(part.to, line.end);
      if (a >= b) return;
      const text = element.text.slice(a, b);
      if (part.op === 'same' || part.op === 'gap') out.push(<React.Fragment key={i}>{text}</React.Fragment>);
      else out.push(<mark key={i} style={{ ...(part.op === 'add' ? ADDED : REMOVED), borderRadius: 3 }} data-review-word={part.op}>{text}</mark>);
    });
    return out;
  };

  return (
    <div className="flex flex-col items-center" style={{ gap: pageGap, padding: `${padTop}px 0 ${pageGap}px` }} data-screenplay-review>
      {!!titlePage.title && <TitlePageView titlePage={titlePage} paper={paper} scale={scale} />}
      {pages.map(page => (
        <Sheet key={page.number} paper={paper} scale={scale} data={String(page.number)}>
          {page.number > 1 && <span className="absolute" style={{ right: m.right, top: 36 * scale }}>{page.number}.</span>}
          <div className="absolute overflow-hidden whitespace-pre" style={{ left: m.left, top: m.top, right: m.right, bottom: m.top }}>
            {page.lines.map((line, i) => {
              const row = line.element >= 0 ? rows[line.element] : null;
              const style: React.CSSProperties = { paddingLeft: line.indent * m.charWidth, height: m.lineHeight, fontWeight: line.kind === 'scene_heading' ? 700 : 400, borderRadius: 3 };
              if (row?.op === 'add') Object.assign(style, ADDED);
              if (row?.op === 'del') Object.assign(style, REMOVED);
              // A changed line: its words told apart; a line changed only in kind is shown whole as it is now
              const words = row?.op === 'change' && row.pieces?.some(piece => piece.op !== 'same') ? runsOn(row, elements[line.element], line.row) : line.text;
              return <div key={i} style={style} data-review-op={row?.op && row.op !== 'same' ? row.op : undefined}>{words}</div>;
            })}
          </div>
        </Sheet>
      ))}
    </div>
  );
};

export default ReviewPages;
